<script setup>
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import OnboardingWelcome from './components/OnboardingWelcome.vue';
import OnboardingChannel from './components/OnboardingChannel.vue';
import OnboardingAssistant from './components/OnboardingAssistant.vue';
import OnboardingCapabilities from './components/OnboardingCapabilities.vue';
import OnboardingVenueReview from './components/OnboardingVenueReview.vue';
import OnboardingSpaceReview from './components/OnboardingSpaceReview.vue';
import OnboardingSetupProgress from './components/OnboardingSetupProgress.vue';
import OnboardingFinish from './components/OnboardingFinish.vue';
import OnboardingOverview from './components/OnboardingOverview.vue';
import OnboardingPhaseIntro from './components/OnboardingPhaseIntro.vue';
import OnboardingBookCall from './components/OnboardingBookCall.vue';
import OnboardingAiEntry from './components/OnboardingAiEntry.vue';
import { ONBOARDING_NAV_KEY } from './useOnboardingNav';

// Getting Started micro-phases (within product phase 1)
const GETTING_STARTED_STEPS = [
  [0], // domain
  [1, 2], // venue + spaces
  [3], // email channel
  [6, 7], // setup / finish
];

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const restartDialogRef = ref(null);

// hub | phaseIntro | step | aiEntry | aiWidget | bookCall
const screenType = ref('hub');
const productPhase = ref(null); // getting_started | starting_with_ai | pricing | growth
const growthTrack = ref(null); // automate | quotes
const phaseIndex = ref(0);
const statusReady = ref(false);
const ignoreStepWatch = ref(false);

const stepNav = ref({
  primary: null,
  secondary: null,
  onBack: null,
  hideBack: false,
  hidePrimary: false,
});

const step = computed(() => store.state.onboarding.step);
const domain = computed(() => store.state.onboarding.domain);
const uiFlags = computed(() => store.state.onboarding.uiFlags);
const lindexResult = computed(() => store.state.onboarding.lindexResult);
const lindexError = computed(() => store.state.onboarding.lindexError);
const hub = computed(() => store.state.onboarding.hub);
const isReviewPending = computed(
  () =>
    uiFlags.value.isPolling &&
    !lindexResult.value?.spaces?.length &&
    !lindexError.value
);

const venueName = computed(
  () =>
    store.state.onboarding.reviewVenue?.name ||
    lindexResult.value?.venue?.name ||
    store.state.onboarding.domain
);

function phaseForGettingStartedStep(stepIndex) {
  const index = GETTING_STARTED_STEPS.findIndex(steps =>
    steps.includes(stepIndex)
  );
  return index >= 0 ? index : 0;
}

function isFirstStepOfGettingStartedPhase(stepIndex) {
  const phase = phaseForGettingStartedStep(stepIndex);
  return GETTING_STARTED_STEPS[phase][0] === stepIndex;
}

function lastStepOfGettingStartedPhase(phase) {
  const steps = GETTING_STARTED_STEPS[phase];
  return steps[steps.length - 1];
}

const progressFills = computed(() => {
  if (screenType.value === 'hub' || screenType.value === 'bookCall') {
    return [];
  }

  if (productPhase.value === 'starting_with_ai') {
    const aiScreens = ['aiEntry', 'aiWidget', 'step'];
    const active =
      screenType.value === 'aiEntry'
        ? 0
        : screenType.value === 'aiWidget'
          ? 1
          : step.value <= 4
            ? 2
            : step.value === 5
              ? 3
              : 4;
    return [0, 1, 2, 3, 4].map(index => (index < active ? 1 : index === active ? 0.5 : 0));
  }

  if (productPhase.value !== 'getting_started') return [];

  if (screenType.value === 'phaseIntro') {
    return GETTING_STARTED_STEPS.map((_, index) =>
      index < phaseIndex.value ? 1 : index === phaseIndex.value ? 0.08 : 0
    );
  }

  const activePhase = phaseForGettingStartedStep(step.value);
  return GETTING_STARTED_STEPS.map((steps, index) => {
    if (index < activePhase) return 1;
    if (index > activePhase) return 0;
    const position = steps.indexOf(step.value);
    if (position < 0) return 0;
    return (position + 1) / steps.length;
  });
});

const showFooter = computed(
  () => screenType.value !== 'hub' && progressFills.value.length > 0
);

const showBack = computed(() => {
  if (screenType.value === 'hub') return false;
  if (screenType.value === 'step' && stepNav.value.hideBack) return false;
  return true;
});

const backLabel = computed(() => {
  if (typeof stepNav.value.onBack === 'function' && stepNav.value.backLabel) {
    return stepNav.value.backLabel;
  }
  return t('ONBOARDING_WIZARD.BACK');
});

const primaryAction = computed(() => {
  if (screenType.value === 'phaseIntro') {
    return {
      label: t('ONBOARDING_WIZARD.NEXT'),
      disabled: false,
      loading: false,
      hide: false,
      onClick: continueFromPhaseIntro,
    };
  }

  const primary = stepNav.value.primary;
  if (!primary || stepNav.value.hidePrimary) {
    return { hide: true };
  }

  return {
    label: primary.label,
    disabled: Boolean(primary.disabled),
    loading: Boolean(primary.loading),
    hide: false,
    onClick: () => primary.onClick?.(),
  };
});

const secondaryAction = computed(() => {
  if (screenType.value !== 'step') return null;
  return stepNav.value.secondary || null;
});

function registerNav(config = {}) {
  stepNav.value = {
    primary: config.primary || null,
    secondary: config.secondary || null,
    onBack: config.onBack || null,
    backLabel: config.backLabel || null,
    hideBack: Boolean(config.hideBack),
    hidePrimary: Boolean(config.hidePrimary),
  };
}

function clearNav() {
  stepNav.value = {
    primary: null,
    secondary: null,
    onBack: null,
    backLabel: null,
    hideBack: false,
    hidePrimary: false,
  };
}

provide(ONBOARDING_NAV_KEY, {
  registerNav,
  clearNav,
});

function returnToHub() {
  clearNav();
  productPhase.value = null;
  growthTrack.value = null;
  phaseIndex.value = 0;
  screenType.value = 'hub';
}

function startPhase({ phase, track = null }) {
  productPhase.value = phase;
  growthTrack.value = track;

  if (phase === 'pricing' || phase === 'growth') {
    screenType.value = 'bookCall';
    return;
  }

  if (phase === 'starting_with_ai') {
    screenType.value = 'aiEntry';
    return;
  }

  // getting_started
  if (step.value > 0 || domain.value) {
    phaseIndex.value = phaseForGettingStartedStep(step.value);
    // Map legacy assistant steps into finish path if somehow stuck
    if ([4, 5].includes(step.value)) {
      ignoreStepWatch.value = true;
      store.dispatch('onboarding/setStep', 6).finally(() => {
        ignoreStepWatch.value = false;
      });
      phaseIndex.value = phaseForGettingStartedStep(6);
    }
    screenType.value = 'step';
    return;
  }

  phaseIndex.value = 0;
  screenType.value = 'phaseIntro';
}

function continueFromPhaseIntro() {
  const targetStep = GETTING_STARTED_STEPS[phaseIndex.value][0];
  if (step.value !== targetStep) {
    ignoreStepWatch.value = true;
    store.dispatch('onboarding/setStep', targetStep).finally(() => {
      ignoreStepWatch.value = false;
    });
  }
  screenType.value = 'step';
}

function onAiEntrySelect(choice) {
  if (choice === 'widget') {
    store.dispatch('onboarding/setChannelType', 'web_widget');
    screenType.value = 'aiWidget';
    return;
  }

  const emailTypes = [
    'Channel::Email',
    'Channel::Microsoft',
    'email',
    'microsoft',
  ];
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  const current = store.state.onboarding.inbox;
  const selected =
    (current?.id && emailTypes.includes(current.channel_type) && current) ||
    inboxes.find(item => emailTypes.includes(item.channel_type)) ||
    (current?.id && current) ||
    inboxes[0] ||
    null;

  if (selected) {
    const isMicrosoft =
      selected.channel_type === 'Channel::Microsoft' ||
      selected.channel_type === 'microsoft';
    store.dispatch(
      'onboarding/setChannelType',
      isMicrosoft ? 'microsoft' : 'email'
    );
    store.commit('onboarding/SET_INBOX', {
      id: selected.id,
      name: selected.name,
      channel_type: selected.channel_type,
      email: selected.email,
      forward_to_email: selected.forward_to_email,
      provider: selected.provider,
    });
  }

  // existing channel → assistant
  ignoreStepWatch.value = true;
  store
    .dispatch('onboarding/setStep', {
      step: 4,
      inboxId: selected?.id,
    })
    .finally(() => {
      ignoreStepWatch.value = false;
    });
  screenType.value = 'step';
}

function goBack() {
  if (typeof stepNav.value.onBack === 'function') {
    stepNav.value.onBack();
    return;
  }

  if (
    screenType.value === 'bookCall' ||
    screenType.value === 'aiEntry' ||
    screenType.value === 'aiWidget'
  ) {
    if (screenType.value === 'aiWidget') {
      screenType.value = 'aiEntry';
      return;
    }
    returnToHub();
    return;
  }

  if (screenType.value === 'phaseIntro') {
    if (phaseIndex.value === 0) {
      returnToHub();
      return;
    }
    const previousPhase = phaseIndex.value - 1;
    phaseIndex.value = previousPhase;
    ignoreStepWatch.value = true;
    store
      .dispatch(
        'onboarding/setStep',
        lastStepOfGettingStartedPhase(previousPhase)
      )
      .finally(() => {
        ignoreStepWatch.value = false;
      });
    screenType.value = 'step';
    return;
  }

  if (screenType.value !== 'step') return;

  if (productPhase.value === 'starting_with_ai') {
    if (step.value === 4) {
      screenType.value = 'aiEntry';
      return;
    }
    if (step.value === 5) {
      store.dispatch('onboarding/setStep', 4);
      return;
    }
    returnToHub();
    return;
  }

  if (isFirstStepOfGettingStartedPhase(step.value)) {
    phaseIndex.value = phaseForGettingStartedStep(step.value);
    screenType.value = 'phaseIntro';
    return;
  }

  // Skip assistant steps when going back within getting started
  const prev = step.value === 6 ? 3 : step.value - 1;
  store.dispatch('onboarding/setStep', prev);
}

watch(step, (newStep, oldStep) => {
  if (!statusReady.value || ignoreStepWatch.value) return;
  if (typeof oldStep !== 'number') return;

  // After provisioning a web widget in the AI phase, continue into assistant steps.
  if (
    productPhase.value === 'starting_with_ai' &&
    screenType.value === 'aiWidget' &&
    newStep >= 4
  ) {
    screenType.value = 'step';
    return;
  }

  if (screenType.value !== 'step') return;
  if (productPhase.value !== 'getting_started') return;

  if (newStep > oldStep && isFirstStepOfGettingStartedPhase(newStep)) {
    const newPhase = phaseForGettingStartedStep(newStep);
    if (phaseForGettingStartedStep(oldStep) !== newPhase) {
      phaseIndex.value = newPhase;
      screenType.value = 'phaseIntro';
      return;
    }
  }

  phaseIndex.value = phaseForGettingStartedStep(newStep);
});

onMounted(async () => {
  await store.dispatch('onboarding/fetchStatus');
  await store.dispatch('onboarding/fetchProcedureTemplates');
  if (store.getters['onboarding/isExtractionPending']) {
    store.dispatch('onboarding/startPolling');
  }

  // Always land on hub; user picks a phase
  screenType.value = 'hub';
  statusReady.value = true;
});

onBeforeUnmount(() => {
  store.dispatch('onboarding/stopPolling');
});

watch(lindexResult, result => {
  if (
    result?.spaces?.length &&
    store.state.onboarding.reviewSpaces.length === 0
  ) {
    store.commit('onboarding/SET_REVIEW_SPACES', result.spaces);
  }
  if (result?.venue && !store.state.onboarding.reviewVenue?.name) {
    store.commit('onboarding/SET_REVIEW_VENUE', {
      name: result.venue.name || store.state.onboarding.domain || '',
      address: result.venue.address || '',
      city: result.venue.city || '',
      country: result.venue.country || '',
      website:
        result.venue.website ||
        result.venue.domain ||
        store.state.onboarding.websiteOrigin ||
        '',
    });
  }
});

const skipToManual = () => {
  router.push(accountScopedRoute('settings_inbox_new'));
};

const openRestartConfirm = () => {
  restartDialogRef.value?.open();
};

const restartOnboarding = async () => {
  await store.dispatch('onboarding/restart');
  restartDialogRef.value?.close();
  clearNav();
  returnToHub();
};

const finishGettingStarted = async () => {
  await store.dispatch('onboarding/complete');
  returnToHub();
};

const finishAiPhase = async () => {
  await store.dispatch('onboarding/completePhase', {
    phase: 'starting_with_ai',
  });
  returnToHub();
};

watch(
  () => hub.value?.visible,
  visible => {
    if (statusReady.value && visible === false) {
      router.push(accountScopedRoute('copilot_home'));
    }
  }
);
</script>

<template>
  <section
    class="relative flex flex-col flex-1 w-full min-h-screen overflow-hidden bg-n-surface-1"
  >
    <header
      class="shrink-0 z-10 flex items-center justify-end gap-2 w-full px-6 pt-6 pb-4 bg-n-surface-1"
    >
      <Button
        v-if="screenType !== 'hub'"
        lg
        slate
        outline
        class="bg-n-surface-1"
        icon="i-lucide-layout-list"
        :label="t('ONBOARDING_WIZARD.RETURN_TO_HUB')"
        @click="returnToHub"
      />
      <Button
        v-if="productPhase === 'getting_started'"
        lg
        slate
        outline
        class="bg-n-surface-1"
        icon="i-lucide-rotate-ccw"
        :label="t('ONBOARDING_WIZARD.RESTART')"
        @click="openRestartConfirm"
      />
      <Button
        lg
        slate
        outline
        class="bg-n-surface-1"
        icon="i-lucide-settings"
        :label="t('ONBOARDING_WIZARD.SKIP_MANUAL')"
        @click="skipToManual"
      />
    </header>

    <div class="flex-1 overflow-y-auto">
      <div
        class="min-h-full flex flex-col items-center justify-center px-6 py-10"
      >
        <OnboardingOverview
          v-if="screenType === 'hub'"
          @start-phase="startPhase"
          @open-home="router.push(accountScopedRoute('copilot_home'))"
        />
        <OnboardingBookCall
          v-else-if="screenType === 'bookCall'"
          :phase="productPhase"
          :track="growthTrack"
          @back="returnToHub"
          @complete="returnToHub"
        />
        <OnboardingAiEntry
          v-else-if="screenType === 'aiEntry'"
          @back="returnToHub"
          @select="onAiEntrySelect"
        />
        <OnboardingChannel
          v-else-if="screenType === 'aiWidget'"
          variant="ai_widget"
        />
        <OnboardingPhaseIntro
          v-else-if="screenType === 'phaseIntro'"
          :phase-index="phaseIndex"
        />
        <template v-else>
          <OnboardingWelcome v-if="step === 0" />
          <OnboardingVenueReview v-else-if="step === 1" />
          <OnboardingSpaceReview
            v-else-if="step === 2"
            :venue-name="venueName"
            :is-pending="isReviewPending"
          />
          <OnboardingChannel
            v-else-if="step === 3"
            variant="getting_started"
          />
          <OnboardingAssistant v-else-if="step === 4" />
          <OnboardingCapabilities
            v-else-if="step === 5"
            :venue-name="venueName"
          />
          <OnboardingSetupProgress
            v-else-if="step === 6"
            :variant="
              productPhase === 'starting_with_ai'
                ? 'starting_with_ai'
                : 'getting_started'
            "
            @complete="store.dispatch('onboarding/setStep', 7)"
          />
          <OnboardingFinish
            v-else
            @complete="
              productPhase === 'starting_with_ai'
                ? finishAiPhase()
                : finishGettingStarted()
            "
          />
        </template>
      </div>
    </div>

    <footer
      v-if="showFooter || primaryAction.hide === false || showBack"
      class="w-full shrink-0 bg-n-surface-1 border-t border-slate-200 dark:border-slate-800"
    >
      <div
        v-if="progressFills.length"
        class="w-full px-6 pt-3 flex gap-2"
        aria-hidden="true"
      >
        <div
          v-for="(fill, index) in progressFills"
          :key="index"
          class="h-1 flex-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
        >
          <div
            class="h-full bg-slate-900 dark:bg-white transition-all duration-300 ease-out"
            :style="{ width: `${Math.round(fill * 100)}%` }"
          />
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 w-full px-6 py-4">
        <div class="min-w-[6rem]">
          <Button
            v-if="showBack"
            lg
            slate
            link
            :label="backLabel"
            @click="goBack"
          />
        </div>

        <div class="flex items-center justify-end gap-3 min-w-0">
          <Button
            v-if="secondaryAction"
            lg
            slate
            outline
            :disabled="secondaryAction.disabled"
            :is-loading="secondaryAction.loading"
            :label="secondaryAction.label"
            @click="secondaryAction.onClick?.()"
          />
          <Button
            v-if="!primaryAction.hide"
            lg
            icon="i-lucide-arrow-right"
            trailing-icon
            :disabled="primaryAction.disabled"
            :is-loading="primaryAction.loading"
            :label="primaryAction.label"
            @click="primaryAction.onClick?.()"
          />
        </div>
      </div>
    </footer>

    <Dialog
      ref="restartDialogRef"
      type="alert"
      width="sm"
      :title="t('ONBOARDING_WIZARD.RESTART_CONFIRM_TITLE')"
      :description="t('ONBOARDING_WIZARD.RESTART_CONFIRM_DESCRIPTION')"
      :cancel-button-label="t('ONBOARDING_WIZARD.CANCEL')"
      :confirm-button-label="t('ONBOARDING_WIZARD.RESTART_CONFIRM')"
      @confirm="restartOnboarding"
    />

  </section>
</template>
